import { Router } from "express";
import { JobController } from "../controllers/job.controller";
import { Routes } from "../../../interface/routes.interface";
import { permissionInjector } from "../../../middlewares/permissionInjector";
import { authMiddleware } from "middlewares/authMiddleware";

/**
 * @swagger
 * tags:
 *   name: Job
 *   description: Job Management APIs
 */

export class JobRouter implements Routes {
    public router = Router();
    public jobController = new JobController();
    public path = '/job';

    constructor() {
        this.initializeRoutes();
    }

    public initializeRoutes() {
        /**
         * @swagger
         * /job:
         *   post:
         *     summary: Create a new job
         *     tags: [Job]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               companyName:
         *                 type: string
         *                 description: Name of the company or field (optional, managed internally if linked to AllocationRequest)
         *               location:
         *                 type: string
         *                 description: Location of the job (optional, managed internally if linked to AllocationRequest)
         *               noOfSpots:
         *                 type: integer
         *                 description: Number of labor positions needed
         *                 minimum: 1
         *               note:
         *                 type: string
         *                 description: Additional notes for the job
         *             required:
         *               - noOfSpots
         *     responses:
         *       201:
         *         description: Job created successfully
         *       400:
         *         description: Bad request (e.g., invalid noOfSpots)
         *       401:
         *         description: Unauthorized
         *       409:
         *         description: Job already exists or invalid data
         */
        this.router.post(`${this.path}`, authMiddleware, permissionInjector(`${this.path}/create`, 'POST'), this.jobController.createJob);

        /**
         * @swagger
         * /job:
         *   get:
         *     summary: Get list of jobs
         *     tags: [Job]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: search
         *         schema:
         *           type: string
         *         description: Search by company/field name or location
         *       - in: query
         *         name: filter
         *         schema:
         *           type: string
         *           enum: [Open, Closed]
         *         description: Filter by job status (Open or Closed)
         *       - in: query
         *         name: sort
         *         schema:
         *           type: string
         *           enum: [createdDateAsc, createdDateDesc, companyNameAsc, companyNameDesc, noOfSpotsAsc, noOfSpotsDesc, allottedAsc, allottedDesc, openAsc, openDesc]
         *         description: Sort by field (e.g., createdDateAsc)
         *     responses:
         *       200:
         *         description: List of jobs
         *       401:
         *         description: Unauthorized
         */
        this.router.get(`${this.path}`, authMiddleware, permissionInjector(`${this.path}/list`, 'GET'), this.jobController.getJobs);
        /**
         * @swagger
         * /job/getOpen:
         *   get:
         *     summary: Get list of open jobs
         *     tags: [Job]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: search
         *         schema:
         *           type: string
         *         description: Search by company/field name or location
         *       - in: query
         *         name: sort
         *         schema:
         *           type: string
         *           enum: [createdDateAsc, createdDateDesc, companyNameAsc, companyNameDesc, noOfSpotsAsc, noOfSpotsDesc, allottedAsc, allottedDesc, openAsc, openDesc]
         *         description: Sort by field (e.g., createdDateAsc)
         *     responses:
         *       200:
         *         description: List of jobs
         *       401:
         *         description: Unauthorized
         */
        this.router.get(`${this.path}/getOpen`, authMiddleware, permissionInjector(`${this.path}/list`, 'GET'), this.jobController.getOpneJobs);


        /**
         * @swagger
         * /job/{id}:
         *   get:
         *     summary: Get job details by ID
         *     tags: [Job]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the job
         *     responses:
         *       200:
         *         description: Job details
         *       404:
         *         description: Job not found
         *       401:
         *         description: Unauthorized
         */
        this.router.get(`${this.path}/:id`, authMiddleware, permissionInjector(`${this.path}/view`, 'GET'), this.jobController.getJobById);

        /**
         * @swagger
         * /job/{id}:
         *   put:
         *     summary: Update job by ID
         *     tags: [Job]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the job
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               companyName:
         *                 type: string
         *                 description: Name of the company or field (optional)
         *               location:
         *                 type: string
         *                 description: Location of the job (optional)
         *               noOfSpots:
         *                 type: integer
         *                 description: Number of labor positions needed
         *                 minimum: 1
         *               note:
         *                 type: string
         *                 description: Additional notes for the job
         *             required:
         *               - noOfSpots
         *     responses:
         *       200:
         *         description: Job updated
         *       400:
         *         description: Bad request (e.g., invalid noOfSpots or cannot reduce below allotted)
         *       404:
         *         description: Job not found
         *       401:
         *         description: Unauthorized
         */
        this.router.put(`${this.path}/:id`, authMiddleware, permissionInjector(`${this.path}/update`, 'PUT'), this.jobController.updateJob);

        /**
         * @swagger
         * /job:
         *   delete:
         *     summary: Delete job by ID
         *     tags: [Job]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               ids:
         *                 type: array
         *                 items:
         *                   type: string
         *                 description: Array of rating IDs to delete
         *     responses:
         *       200:
         *         description: Job deleted
         *       404:
         *         description: Job not found
         *       401:
         *         description: Unauthorized
         */
        this.router.delete(`${this.path}`, authMiddleware, permissionInjector(`${this.path}/delete`, 'DELETE'), this.jobController.deleteJob);

        /**
         * @swagger
         * /job/{id}/close:
         *   put:
         *     summary: Close job by ID
         *     tags: [Job]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the job
         *     responses:
         *       200:
         *         description: Job closed
         *       400:
         *         description: Cannot close job (e.g., allotted spots do not match noOfSpots)
         *       404:
         *         description: Job not found
         *       401:
         *         description: Unauthorized
         */
        this.router.put(`${this.path}/:id/close`, authMiddleware, permissionInjector(`${this.path}/close`, 'PUT'), this.jobController.closeJob);
    }
}